import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Customer {
  id: string;
  name: string;
  phone: string;
  weddingDate: string;
  makeupType: string;
  preferredStyle: string;
  skinTone: string;
  lipColor: string;
  eyebrowStyle: string;
  skinIssues: string;
  dressInfo: string;
  accessories: string;
  rehearsalStyle: string;
  rehearsalPhoto: string;
  weddingStyle: string;
  status: string;
}

export default function MakeupCustomers() {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([
    {
      id: '1',
      name: '김미영',
      phone: '010-1234-5678',
      weddingDate: '2024-03-25',
      makeupType: '본식+리허설',
      preferredStyle: '내추럴',
      skinTone: '쿨톤',
      lipColor: '코랄핑크',
      eyebrowStyle: '자연스러운 아치형',
      skinIssues: '민감성 피부, T존 지성',
      dressInfo: '화이트 머메이드 드레스',
      accessories: '티아라, 진주 귀걸이',
      rehearsalStyle: '세미글램 스타일',
      rehearsalPhoto: 'https://readdy.ai/api/search-image?query=elegant%20natural%20bridal%20makeup%20with%20soft%20pink%20lips%20and%20natural%20eyebrows%20on%20fair%20skin%20bride%20wearing%20white%20dress%20in%20bright%20studio%20setting%20professional%20beauty%20photography%20clean%20white%20background&width=400&height=400&seq=rehearsal1&orientation=squarish',
      weddingStyle: '내추럴 글로우',
      status: '진행중'
    },
    {
      id: '2',
      name: '이지은',
      phone: '010-2345-6789',
      weddingDate: '2024-04-10',
      makeupType: '본식',
      preferredStyle: '글램',
      skinTone: '웜톤',
      lipColor: '레드브라운',
      eyebrowStyle: '또렷한 직선형',
      skinIssues: '건성 피부',
      dressInfo: '아이보리 A라인 드레스',
      accessories: '화관, 드롭 귀걸이',
      rehearsalStyle: '-',
      rehearsalPhoto: '',
      weddingStyle: '글램 스타일',
      status: '대기'
    },
    {
      id: '3',
      name: '박수진',
      phone: '010-3456-7890',
      weddingDate: '2024-03-30',
      makeupType: '본식+리허설',
      preferredStyle: '클래식',
      skinTone: '중간톤',
      lipColor: '로즈핑크',
      eyebrowStyle: '부드러운 아치형',
      skinIssues: '복합성 피부',
      dressInfo: '화이트 볼가운 드레스',
      accessories: '베일, 진주 목걸이',
      rehearsalStyle: '클래식 스타일',
      rehearsalPhoto: 'https://readdy.ai/api/search-image?query=classic%20elegant%20bridal%20makeup%20with%20rose%20pink%20lips%20and%20soft%20arched%20eyebrows%20sophisticated%20beauty%20look%20on%20bride%20in%20white%20ballgown%20professional%20wedding%20photography%20clean%20white%20background&width=400&height=400&seq=rehearsal2&orientation=squarish',
      weddingStyle: '클래식 엘레강스',
      status: '완료'
    }
  ]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const newCustomer: Customer = {
      id: selectedCustomer ? selectedCustomer.id : (customers.length + 1).toString(),
      name: formData.get('name') as string,
      phone: formData.get('phone') as string,
      weddingDate: formData.get('weddingDate') as string,
      makeupType: formData.get('makeupType') as string,
      preferredStyle: formData.get('preferredStyle') as string,
      skinTone: formData.get('skinTone') as string,
      lipColor: formData.get('lipColor') as string,
      eyebrowStyle: formData.get('eyebrowStyle') as string,
      skinIssues: formData.get('skinIssues') as string,
      dressInfo: formData.get('dressInfo') as string,
      accessories: formData.get('accessories') as string,
      rehearsalStyle: formData.get('rehearsalStyle') as string,
      rehearsalPhoto: formData.get('rehearsalPhoto') as string,
      weddingStyle: formData.get('weddingStyle') as string,
      status: formData.get('status') as string
    };

    if (selectedCustomer) {
      setCustomers(customers.map(c => c.id === selectedCustomer.id ? newCustomer : c));
    } else {
      setCustomers([...customers, newCustomer]);
    }

    setShowAddForm(false);
    setSelectedCustomer(null);
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowAddForm(true);
  };

  const handleViewDetail = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDetailModal(true);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객 스타일 정보 관리</h1>
            <p className="text-sm text-gray-600 mt-1">고객별 메이크업 컨셉과 스타일을 관리하세요</p>
          </div>
          <button
            onClick={() => {
              setSelectedCustomer(null);
              setShowAddForm(true);
            }}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            고객 등록
          </button>
        </div>

        {/* 고객 목록 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customers.map((customer) => (
            <div key={customer.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-xl text-pink-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                      <p className="text-sm text-gray-600">{customer.phone}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    customer.status === '완료' ? 'bg-green-100 text-green-700' :
                    customer.status === '진행중' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {customer.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-calendar-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    <span>예식일: {customer.weddingDate}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-brush-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    <span>{customer.makeupType}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-palette-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    <span>{customer.preferredStyle} 스타일</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewDetail(customer)}
                    className="flex-1 px-3 py-2 bg-pink-50 text-pink-600 rounded-lg hover:bg-pink-100 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    상세보기
                  </button>
                  <button
                    onClick={() => handleEdit(customer)}
                    className="flex-1 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    수정
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 고객 등록/수정 모달 */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {selectedCustomer ? '고객 정보 수정' : '고객 등록'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedCustomer(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                    <input 
                      type="text" 
                      name="name"
                      defaultValue={selectedCustomer?.name || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                    <input 
                      type="tel" 
                      name="phone"
                      defaultValue={selectedCustomer?.phone || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예식일</label>
                    <input 
                      type="date" 
                      name="weddingDate"
                      defaultValue={selectedCustomer?.weddingDate || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메이크업 유형</label>
                    <select 
                      name="makeupType"
                      defaultValue={selectedCustomer?.makeupType || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      <option value="">선택하세요</option>
                      <option value="본식">본식</option>
                      <option value="리허설">리허설</option>
                      <option value="본식+리허설">본식+리허설</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">선호 스타일</label>
                    <select 
                      name="preferredStyle"
                      defaultValue={selectedCustomer?.preferredStyle || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      <option value="">선택</option>
                      <option value="내추럴">내추럴</option>
                      <option value="세미글램">세미글램</option>
                      <option value="글램">글램</option>
                      <option value="클래식">클래식</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">피부톤</label>
                    <select 
                      name="skinTone"
                      defaultValue={selectedCustomer?.skinTone || ''}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      <option value="">선택</option>
                      <option value="쿨톤">쿨톤</option>
                      <option value="웜톤">웜톤</option>
                      <option value="중간톤">중간톤</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">립 컬러</label>
                    <input 
                      type="text" 
                      name="lipColor"
                      defaultValue={selectedCustomer?.lipColor || ''}
                      placeholder="예: 코랄핑크"
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">눈썹 스타일</label>
                  <input 
                    type="text" 
                    name="eyebrowStyle"
                    defaultValue={selectedCustomer?.eyebrowStyle || ''}
                    placeholder="예: 자연스러운 아치형"
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">피부 트러블/민감 메모</label>
                  <textarea 
                    name="skinIssues"
                    defaultValue={selectedCustomer?.skinIssues || ''}
                    maxLength={500}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 h-20 resize-none"
                    placeholder="피부 타입, 알레르기, 민감 부위 등"
                  ></textarea>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">드레스 정보</label>
                    <input 
                      type="text" 
                      name="dressInfo"
                      defaultValue={selectedCustomer?.dressInfo || ''}
                      placeholder="예: 화이트 머메이드 드레스"
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">액세서리</label>
                    <input 
                      type="text" 
                      name="accessories"
                      defaultValue={selectedCustomer?.accessories || ''}
                      placeholder="예: 티아라, 진주 귀걸이"
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">리허설 스타일</label>
                    <input 
                      type="text" 
                      name="rehearsalStyle"
                      defaultValue={selectedCustomer?.rehearsalStyle || ''}
                      placeholder="리허설 메이크업 스타일"
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">본식 스타일</label>
                    <input 
                      type="text" 
                      name="weddingStyle"
                      defaultValue={selectedCustomer?.weddingStyle || ''}
                      placeholder="본식 메이크업 스타일"
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">리허설 사진 URL</label>
                  <input 
                    type="text" 
                    name="rehearsalPhoto"
                    defaultValue={selectedCustomer?.rehearsalPhoto || ''}
                    placeholder="리허설 메이크업 사진 URL"
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                  <select 
                    name="status"
                    defaultValue={selectedCustomer?.status || '대기'}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                  >
                    <option value="대기">대기</option>
                    <option value="진행중">진행중</option>
                    <option value="완료">완료</option>
                  </select>
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedCustomer(null);
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 whitespace-nowrap cursor-pointer"
                  >
                    {selectedCustomer ? '수정' : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 고객 상세 모달 */}
        {showDetailModal && selectedCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">고객 스타일 컨셉 시트</h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedCustomer(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* 기본 정보 */}
                <div className="bg-pink-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">기본 정보</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">고객명</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">연락처</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.phone}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">예식일</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.weddingDate}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">메이크업 유형</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.makeupType}</div>
                    </div>
                  </div>
                </div>

                {/* 스타일 정보 */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">스타일 정보</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">선호 스타일</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.preferredStyle}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">피부톤</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.skinTone}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">립 컬러</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.lipColor}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="text-sm text-gray-600">눈썹 스타일</div>
                    <div className="font-medium text-gray-900">{selectedCustomer.eyebrowStyle}</div>
                  </div>
                </div>

                {/* 피부 정보 */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">피부 정보</h3>
                  <div className="text-gray-900">{selectedCustomer.skinIssues}</div>
                </div>

                {/* 의상 정보 */}
                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">의상 & 액세서리</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">드레스</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.dressInfo}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">액세서리</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.accessories}</div>
                    </div>
                  </div>
                </div>

                {/* 메이크업 스타일 */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">메이크업 스타일</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">리허설 스타일</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.rehearsalStyle || '-'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">본식 스타일</div>
                      <div className="font-medium text-gray-900">{selectedCustomer.weddingStyle}</div>
                    </div>
                  </div>
                </div>

                {/* 리허설 사진 */}
                {selectedCustomer.rehearsalPhoto && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">리허설 메이크업 사진</h3>
                    <img 
                      src={selectedCustomer.rehearsalPhoto} 
                      alt="리허설 메이크업"
                      className="w-full max-w-md rounded-lg shadow-md object-cover object-top"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-printer-line mr-2"></i>
                    출력
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEdit(selectedCustomer);
                    }}
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    수정
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
